import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchOpponentTeams, fetchUserTeams } from '../../api/userTeams';
import { fetchCampaignContestByID } from '../../api/campaigns';
import { startGame, startGameWithSubs } from '../../api/games';
import { fetchUserCardInventory, fetchCardsByUserID } from '../../api/cards';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { toast } from 'react-toastify';
import { filterTempTeams, filterSelectableUserTeams } from '../../helpers/teamHelpers';
import FatiguedSubstitutionModal from '../../components/FatiguedSubstitutionModal';
import RosterSubstitutionPanel from '../../components/TeamFatigueStatus';
import BackButton from '../../components/BackButton';
import { handleActiveGameExists } from '../../helpers/startGameConflicts';

const CreateGamePage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const { campaignContestID } = useParams();
    const navigate = useNavigate();
    const [userTeams, setUserTeams] = useState([]);
    const [opponentTeams, setOpponentTeams] = useState([]);
    const [selectedUserTeamID, setSelectedUserTeamID] = useState('');
    const [selectedOpponentTeamID, setSelectedOpponentTeamID] = useState('');
    const [role, setRole] = useState('home');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fatigue substitution state
    const [showSubModal, setShowSubModal] = useState(false);
    const [fatiguedData, setFatiguedData] = useState(null);  // { unavailableCards, availableSubstitutes }
    const [pendingPayload, setPendingPayload] = useState(null);

    // Pre-game fatigue preview
    const [teamInventory, setTeamInventory] = useState(null);
    const [fatigueLoading, setFatigueLoading] = useState(false);

    // Proactive substitutions chosen via the roster panel
    const [substitutions, setSubstitutions] = useState([]);

    const handleSubstitutionsChange = useCallback(subs => {
        setSubstitutions(subs);
    }, []);

    useEffect(() => {
        const fetchUserTeamsData = async () => {
            try {
                const userTeamsData = await fetchUserTeams();
                setUserTeams(filterSelectableUserTeams(userTeamsData));
            } catch (err) {
                toast.error('Error fetching user teams: ' + err.message);
            }
        };

        const loadOpponentTeams = async () => {
            try {
                const opponentTeamsData = await fetchOpponentTeams();
                setOpponentTeams(filterTempTeams(opponentTeamsData));
            } catch (err) {
                toast.error('Error fetching opponent teams: ' + err.message);
            }
        };

        const fetchContestDetails = async () => {
            if (campaignContestID) {
                try {
                    const campaignContest = await fetchCampaignContestByID(campaignContestID);
                    const randomRole = Math.random() < 0.5 ? 'home' : 'away';
                    setRole(randomRole);
                    setSelectedUserTeamID(campaignContest.userTeamID);
                } catch (err) {
                    toast.error('Error fetching campaign contest: ' + err.message);
                }
            }
        };

        if (currentUser) {
            fetchUserTeamsData();
            loadOpponentTeams();
            fetchContestDetails();
        }
    }, [currentUser, campaignContestID]);

    // The team the user is controlling — always their selected team regardless of role
    const userTeamID = selectedUserTeamID;

    // Fetch card inventory (with fatigue) whenever the user's team selection changes
    useEffect(() => {
        if (!userTeamID || userTeams.length === 0) {
            setTeamInventory(null);
            return;
        }

        const selectedTeam = userTeams.find(t => String(t.userTeamID) === String(userTeamID));
        if (!selectedTeam?.UserTeamLineups) {
            setTeamInventory(null);
            return;
        }

        const cardIDs = selectedTeam.UserTeamLineups
            .map(ul => ul.cardID)
            .filter(Boolean);

        if (cardIDs.length === 0) {
            setTeamInventory(null);
            return;
        }

        const fetchFatigue = async () => {
            setFatigueLoading(true);
            try {
                const teamCardIDSet = new Set(cardIDs.map(String));

                const [inventoryData, allUserCardsData] = await Promise.all([
                    fetchUserCardInventory(cardIDs),
                    fetchCardsByUserID(),
                ]);

                const extraCardIDs = (allUserCardsData.cards ?? [])
                    .filter(ci => ci.Card && !teamCardIDSet.has(String(ci.Card.cardID)))
                    .map(ci => ci.Card.cardID);

                const extraInventory = extraCardIDs.length > 0
                    ? await fetchUserCardInventory(extraCardIDs)
                    : [];

                setTeamInventory({
                    inventoryData: [...inventoryData, ...extraInventory],
                    lineup: selectedTeam.UserTeamLineups,
                });
            } catch {
                // Non-critical — fatigue display is best-effort
                setTeamInventory(null);
            } finally {
                setFatigueLoading(false);
            }
        };

        fetchFatigue();
    }, [userTeamID, userTeams]);

    const buildPayload = () => ({
        homeUserTeamID: role === 'home' ? selectedUserTeamID : selectedOpponentTeamID,
        visitingUserTeamID: role === 'home' ? selectedOpponentTeamID : selectedUserTeamID,
        role,
        campaignContestID: campaignContestID ? campaignContestID : null,
    });

    const handleSubmit = async event => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const newGame = await startGame(buildPayload());
            toast.success('Game created successfully!');
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            if (handleActiveGameExists(err, navigate)) return;
            if (err.response?.status === 409 && err.response?.data?.code === 'PLAYERS_FATIGUED') {
                setFatiguedData({
                    unavailableCards: err.response.data.unavailableCards,
                    availableSubstitutes: err.response.data.availableSubstitutes,
                });
                setPendingPayload(err.response.data.pendingPayload);
                setShowSubModal(true);
            } else {
                const msg = err.response?.data?.error || err.message;
                setError(msg);
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubConfirm = useCallback(async substitutions => {
        setShowSubModal(false);
        setLoading(true);
        setError(null);

        try {
            const newGame = await startGameWithSubs({ ...pendingPayload, substitutions });
            toast.success('Game created — tired players were substituted automatically.');
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            const msg = err.response?.data?.error || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [pendingPayload]);

    const handleAllHealed = useCallback(async () => {
        setShowSubModal(false);
        setFatiguedData(null);
        setLoading(true);
        setError(null);
        try {
            const newGame = await startGame(pendingPayload);
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            if (handleActiveGameExists(err, navigate)) return;
            toast.error(err.response?.data?.error || 'Failed to start game');
        } finally {
            setLoading(false);
            setPendingPayload(null);
        }
    }, [pendingPayload, navigate]);

    const handleSubCancel = useCallback(() => {
        setShowSubModal(false);
        setFatiguedData(null);
        setPendingPayload(null);
    }, []);

    return (
        <div className="create-game-page">
            {error && <p className="error">{error}</p>}
            <div className="game-form-card">
                <div className="banner-header">
                    <BackButton to="/games/user" />
                    <h2>Create Game</h2>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="userTeam">Your Team</label>
                        <select
                            id="userTeam"
                            value={selectedUserTeamID}
                            onChange={e => setSelectedUserTeamID(e.target.value)}
                            required
                            disabled={!!campaignContestID}
                        >
                            <option value="">Select your team</option>
                            {userTeams.map(team => (
                                <option key={team.userTeamID} value={team.userTeamID}>
                                    {team.name} ({team.score || 0})
                                </option>
                            ))}
                        </select>
                    </div>
                    {userTeamID && (
                        <RosterSubstitutionPanel
                            teamLineup={teamInventory?.lineup}
                            inventoryData={teamInventory?.inventoryData}
                            loading={fatigueLoading}
                            onSubstitutionsChange={handleSubstitutionsChange}
                        />
                    )}
                    <div className="form-group">
                        <label htmlFor="opponentTeam">Opponent Team</label>
                        <select
                            id="opponentTeam"
                            value={selectedOpponentTeamID}
                            onChange={e => setSelectedOpponentTeamID(e.target.value)}
                            required
                        >
                            <option value="">Select opponent team</option>
                            {opponentTeams.map(team => (
                                <option key={team.userTeamID} value={team.userTeamID}>
                                    {team.name} ({team.score || 0}){team.User ? ` — ${team.User.username}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Role</label>
                        <div className="role-toggle">
                            <label className={role === 'home' ? 'active' : ''}>
                                <input
                                    type="radio"
                                    value="home"
                                    checked={role === 'home'}
                                    onChange={() => setRole('home')}
                                />
                                Home
                            </label>
                            <label className={role === 'away' ? 'active' : ''}>
                                <input
                                    type="radio"
                                    value="away"
                                    checked={role === 'away'}
                                    onChange={() => setRole('away')}
                                />
                                Away
                            </label>
                        </div>
                    </div>
                    <button type="submit" className="btn-primary" disabled={loading || fatigueLoading}>
                        {loading ? 'Creating...' : fatigueLoading ? 'Checking fatigue...' : 'Create Game'}
                    </button>
                </form>
            </div>
            {showSubModal && fatiguedData && (
                <FatiguedSubstitutionModal
                    unavailableCards={fatiguedData.unavailableCards}
                    availableSubstitutes={fatiguedData.availableSubstitutes}
                    onConfirm={handleSubConfirm}
                    onCancel={handleSubCancel}
                    onAllHealed={handleAllHealed}
                    title="Some Players Need Rest"
                />
            )}
        </div>
    );
};

export default CreateGamePage;
